import React, { useEffect, useRef, useState } from 'react';
import { SheetLocation } from './types';
import { resolveCoordinates, calculateOsmTiles, drawOsmPinAndLabels } from './osmTileHelper';

interface PropertyLocationMapPreviewProps {
  location: SheetLocation;
}

export const PropertyLocationMapPreview: React.FC<PropertyLocationMapPreviewProps> = ({ location }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [mapImageUrl, setMapImageUrl] = useState<string>('');
  const [isLoaded, setIsLoaded] = useState(false);

  const coords = resolveCoordinates(
    location.latitude,
    location.longitude,
    location.city,
    location.neighborhood
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const width = container.clientWidth > 100 ? container.clientWidth : 408;
    const height = 160;
    const dpr = 2;

    const canvas = document.createElement('canvas');
    canvas.width = width * dpr;
    canvas.height = height * dpr;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.scale(dpr, dpr);
    ctx.fillStyle = '#f1f5f9';
    ctx.fillRect(0, 0, width, height);

    const zoom = 16;
    const { tiles, centerPixelX, centerPixelY } = calculateOsmTiles(
      coords.lat,
      coords.lng,
      zoom,
      width,
      height
    );

    let loadedCount = 0;
    const totalTiles = tiles.length;
    const label = location.neighborhood || location.city || 'Inmueble';

    const onAllLoaded = () => {
      drawOsmPinAndLabels(ctx, centerPixelX, centerPixelY, label, width, height);
      setMapImageUrl(canvas.toDataURL('image/png'));
      setIsLoaded(true);
    };

    tiles.forEach((tile) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        ctx.drawImage(img, tile.drawX, tile.drawY, 256, 256);
        if (++loadedCount >= totalTiles) onAllLoaded();
      };
      img.onerror = () => {
        if (++loadedCount >= totalTiles) onAllLoaded();
      };
      img.src = tile.url;
    });
  }, [coords.lat, coords.lng, location.neighborhood, location.city]);

  const sectorText = [location.neighborhood, location.city].filter(Boolean).join(' • ');

  return (
    <div className="bg-white rounded-2xl border border-slate-200/90 p-3.5 shadow-2xs">
      {/* Header: Title + Sector tag (No clickable buttons) */}
      <div className="flex items-center justify-between gap-2 mb-2 pb-1.5 border-b border-slate-100">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="w-1.5 h-3.5 rounded-full bg-[#f59e0b] flex-shrink-0" />
          <h3 className="text-xs font-black text-[#0a2540] uppercase leading-normal">
            Ubicación en el Sector
          </h3>
        </div>
        {sectorText && (
          <span className="text-[10px] font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md leading-normal max-w-[55%] truncate">
            {sectorText}
          </span>
        )}
      </div>

      {/* Map Graphic Area */}
      <div ref={containerRef} className="relative w-full h-[160px] rounded-xl overflow-hidden border border-slate-200/80 bg-slate-100">
        {mapImageUrl ? (
          <img
            src={mapImageUrl}
            alt="Mapa de ubicación referencial"
            className="w-full h-full object-cover block"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-xs text-slate-400 font-medium">
            Cargando mapa del sector...
          </div>
        )}
      </div>

      {/* Footer info: address & coordinates */}
      <div className="mt-2 flex items-center justify-between text-[10px] text-slate-500 font-medium px-0.5">
        <span className="truncate max-w-[65%]">{location.address || 'Ubicación referencial'}</span>
        <span className="font-mono text-slate-400 flex-shrink-0">
          {coords.lat.toFixed(4)}°, {coords.lng.toFixed(4)}°
        </span>
      </div>
    </div>
  );
};
